import { useAui } from '@assistant-ui/react';
import StopRoundedIcon from '@mui/icons-material/StopRounded';
import ButtonBase from '@mui/material/ButtonBase';
import { useMutation } from '@tanstack/react-query';

import { Toast } from '#components/toast/toast.js';
import { useTRPC } from '#src/trpc.js';
import { useAddToast } from '#state/stores/toast-store.js';

const BUTTON_CLASSES =
	'inline-flex h-8 w-8 items-center justify-center text-blue-a9 select-none hover:text-blue-11 disabled:opacity-30';

/**
 * Stops the conversation's active run where it is — an explicit server-side
 * interrupt, honoured whichever process is driving the run (this session,
 * another session, or a background worker). A failed interrupt surfaces a toast
 * and leaves the run going, rather than a local cancel that would only detach
 * this viewer while the run continues server-side.
 *
 * The conversation id is read imperatively at click time, not from reactive
 * state: during the send/initialize race the run can already be live
 * server-side while the rendered `remoteId` is still undefined (reactive reads
 * lag a render), and stop must interrupt that run rather than only cancelling
 * the local composer. Only a thread with no server id at all — nothing to stop
 * server-side yet — falls back to the local composer cancel.
 */
export function StopRunButton() {
	const trpc = useTRPC();
	const aui = useAui();
	const addToast = useAddToast();
	const stopRun = useMutation({
		...trpc.agentChatConversation.stopRun.mutationOptions(),
		onError: () => {
			addToast(
				<Toast severity='error' title='Couldn’t stop the run'>
					The run is still going. Please try again.
				</Toast>,
			);
		},
	});

	const handleStop = (): void => {
		const conversationId = aui.threadListItem().getState().remoteId;
		if (conversationId === undefined) {
			aui.composer().cancel();
			return;
		}
		stopRun.mutate({ conversationId });
	};

	return (
		<ButtonBase
			aria-label='Stop generating'
			onClick={handleStop}
			disabled={stopRun.isPending}
			className={BUTTON_CLASSES}
		>
			<StopRoundedIcon fontSize='small' />
		</ButtonBase>
	);
}
